import { useEffect, useRef } from "react";
import { useAppSelector } from "../hooks";
import { useWeb3Auth } from "../hooks/useWeb3Auth";
import { AuthenticateState } from "../types/types";
import api, { getProticoClient } from "../utils/api";
import { getFeatures } from "../config";

/**
 * Hook to handle guest data linking after successful authentication
 * This runs after a user logs in and checks if they chose to link their guest data
 */
export const useGuestDataLinking = () => {
  const { authenticateStatus } = useAppSelector((state) => state.auth);
  const { address, isProticoAuthenticated } = useWeb3Auth();
  const { FEATURE_GUEST_DATA_LINKING_ENABLED } = getFeatures();
  const linkingProcessedRef = useRef(false);

  useEffect(() => {
    // Reset the linking processed flag when user is not authenticated
    if (
      !isProticoAuthenticated ||
      !address ||
      authenticateStatus !== AuthenticateState.WALLET
    ) {
      linkingProcessedRef.current = false;
      return;
    }

    // Only run when user is fully authenticated and feature is enabled
    // Add a small delay to ensure authentication state is fully settled
    if (
      FEATURE_GUEST_DATA_LINKING_ENABLED &&
      isProticoAuthenticated &&
      address &&
      authenticateStatus === AuthenticateState.WALLET &&
      !linkingProcessedRef.current
    ) {
      linkingProcessedRef.current = true; // Mark as processed to prevent duplicates

      // Add a delay to ensure all auth state and API authentication is fully settled
      setTimeout(() => {
        handleGuestDataLinking();
      }, 3000);
    }
  }, [
    FEATURE_GUEST_DATA_LINKING_ENABLED,
    isProticoAuthenticated,
    address,
    authenticateStatus,
  ]);

  const handleGuestDataLinking = async () => {
    try {
      let guestDataToLink = null;
      let linkChoice = false;

      // Check if user chose to link guest data
      try {
        const guestDataStr = localStorage.getItem("protico_guest_data_to_link");
        const linkChoiceStr = localStorage.getItem("protico_guest_link_choice");

        if (guestDataStr) {
          guestDataToLink = JSON.parse(guestDataStr);
        }

        if (linkChoiceStr) {
          linkChoice = linkChoiceStr === "true";
        }
      } catch (error) {
        linkingProcessedRef.current = false; // Reset on error
        return;
      }

      // If user chose not to link data, clean up and return
      if (!linkChoice || !guestDataToLink) {
        cleanupGuestLinkingData();
        linkingProcessedRef.current = false; // Reset since no linking was done
        return;
      }

      // Get authenticated user UUID from ProticoClient instead of API call
      const proticoClient = getProticoClient();
      const userInfo = proticoClient.userInfo;

      if (!userInfo || !userInfo.uuid) {
        console.error(
          "Could not get authenticated user UUID from ProticoClient - user may not be fully authenticated yet"
        );
        linkingProcessedRef.current = false; // Reset since linking failed
        return;
      }

      const authenticatedUserUuid = userInfo.uuid;

      // Call the guest linking API
      const linkResponse = await api.post("/api/guest/link/", {
        guest_uuid: guestDataToLink.guest_uuid,
        authenticated_user_uuid: authenticatedUserUuid,
        link_data: guestDataToLink.link_data,
        // Could add selected_domains here for domain-specific linking in the future
      });

      if (linkResponse.data.success) {
        if (linkResponse.data.data.linked) {
          // Signal that guest linking is complete
          localStorage.setItem("protico_guest_linking_complete", "true");
          // Dispatch event for message components to refresh
          window.dispatchEvent(new CustomEvent("guestLinkingComplete"));
        }
      } else {
        console.error("Failed to link guest data:", linkResponse.data.error);
      }
    } catch (error) {
      console.error("Error during guest data linking:", error);
      linkingProcessedRef.current = false; // Reset on error so user can retry
    } finally {
      // Clean up stored data regardless of success/failure
      cleanupGuestLinkingData();
    }
  };

  const cleanupGuestLinkingData = () => {
    try {
      localStorage.removeItem("protico_guest_data_to_link");
      localStorage.removeItem("protico_guest_link_choice");
    } catch (error) {
      // Silently handle localStorage errors
    }
  };
};

export default useGuestDataLinking;
